import type { IssueSeverity } from '@/shared/constants/issue-status'

import { AttentionList } from './AttentionList'
import { DonutCard } from './DashboardCards'
import { DashTrendCard } from './DashTrendCard'
import { IssueBreakdownCard } from './IssueBreakdownCard'
import { ResolutionHealthCard } from './ResolutionHealthCard'
import { DeltaBadge } from '../DeltaBadge'

import type { BoardShortcuts } from '../../../hooks/useBoardShortcuts'
import type { DashboardInsights } from '../../../hooks/useDashboardInsights'

interface IssuesInsightsPanelProps {
  i: DashboardInsights
  go: BoardShortcuts
  /** Open one issue's full detail (the breakdown's Issues rows). */
  onSelectIssue: (id: string, siteId?: string | null) => void
  /** Toggle a mandal into the page scope (the Needs-attention rows — re-scopes in place). */
  onSelectMandal: (name: string) => void
}

/**
 * The ISSUES dashboard modules — the switchable breakdown (Issues | HOD | Department | Issue type |
 * Question) beside the needs-attention mandals, then the composition row (importance donut ·
 * resolution health · trend). Rendered by the Dashboard tab's Issues section AND the Issues
 * dashboard window, so the two can never drift.
 */
export function IssuesInsightsPanel({ i, go, onSelectIssue, onSelectMandal }: IssuesInsightsPanelProps) {
  return (
    <>
      <div className="grid grid-cols-1 gap-3 @[820px]/dash:grid-cols-2">
        <IssueBreakdownCard
          openIssues={i.openIssues}
          rankedOpen={i.rankedOpen}
          targets={i.targets}
          pending={i.issuesPending}
          onOpenIssue={onSelectIssue}
          go={go}
        />
        <AttentionList mandals={i.scopedMandals} onPick={onSelectMandal} onOpen={() => go.goIssues()} />
      </div>
      <div className="grid grid-cols-1 gap-3 @[620px]/dash:grid-cols-2 @[980px]/dash:grid-cols-3">
        <DonutCard
          title="Open issues by importance"
          segments={i.sevSegments}
          centerValue={i.issuesPending ? '—' : String(i.signals.unresolved)}
          centerLabel="OPEN"
          onPick={(key) => go.goIssues(key as IssueSeverity)}
          onOpen={() => go.goIssues()}
          openLabel="Open Issues board"
        />
        <ResolutionHealthCard
          openIssues={i.openIssues}
          targets={i.targets}
          pending={i.issuesPending}
          onOpen={() => go.goIssues()}
        />
        {/* Third card of the row: full-width when the row wraps to 2 columns (no empty hole),
            back to one column of three at wide. */}
        <div className="min-w-0 @[620px]/dash:col-span-2 @[980px]/dash:col-span-1">
          <DashTrendCard
            title="Active issues trend"
            values={i.trends.issues}
            buckets={i.trendBuckets}
            bucketSize={i.trendBucketSize}
            accent="#dc2626"
            unit=""
            delta={i.issuesDelta !== null ? <DeltaBadge lens="issues" delta={i.issuesDelta} /> : undefined}
          />
        </div>
      </div>
    </>
  )
}
